import type { Race } from '../types';
import { stripTime } from './dateRange';

const MS_PER_DAY = 86_400_000;

function isValidDate(d: Date): boolean {
  return !isNaN(d.getTime());
}

function daysBetween(target: Date, from: Date = new Date()): number {
  if (!isValidDate(target) || !isValidDate(from)) return NaN;
  return Math.round((stripTime(target).getTime() - stripTime(from).getTime()) / MS_PER_DAY);
}

export interface DdayInfo {
  label: string;
  caption: string;
  days: number;
  kind: 'registration-closing' | 'registration-opening' | 'race-day' | 'past' | 'missing';
}

const MISSING: DdayInfo = {
  days: NaN,
  label: '일정 미정',
  caption: '접수 일정 미정',
  kind: 'missing',
};

function toLabel(d: number): string {
  if (!Number.isFinite(d)) return MISSING.label;
  if (d === 0) return 'D-DAY';
  return d > 0 ? `D-${d}` : `D+${-d}`;
}

function buildRaceDayInfo(d: number): DdayInfo {
  if (!Number.isFinite(d)) return MISSING;
  return {
    days: d,
    label: toLabel(d),
    caption: d === 0 ? '오늘 대회' : d > 0 ? '대회일까지' : '대회 종료',
    kind: 'race-day',
  };
}

function buildPastInfo(d: number): DdayInfo {
  if (!Number.isFinite(d)) return MISSING;
  const daysAgo = -d;
  let label: string;
  if (daysAgo < 0) {
    label = toLabel(d); // 데이터 이상: 미래 날짜인데 completed
  } else if (daysAgo === 0) {
    label = '오늘';
  } else if (daysAgo < 30) {
    label = `${daysAgo}일 전`;
  } else if (daysAgo < 365) {
    label = `${Math.round(daysAgo / 30)}개월 전`;
  } else {
    label = `${Math.floor(daysAgo / 365)}년 전`;
  }
  return {
    days: d,
    label,
    caption: '대회 종료',
    kind: 'past',
  };
}

export function getDday(race: Race, now: Date = new Date()): DdayInfo {
  const raceDate = new Date(race.date);
  const regStart = new Date(race.registration?.startDate ?? '');
  const regEnd = new Date(race.registration?.endDate ?? '');

  // 각 상태별 "선호 날짜"가 누락되면 raceDate 로 fallback 하고, raceDate 도 깨지면 MISSING.
  if (race.status === 'open') {
    const d = daysBetween(regEnd, now);
    if (!isNaN(d)) {
      return {
        days: d,
        label: toLabel(d),
        caption: d === 0 ? '오늘 접수 마감' : d > 0 ? '접수 마감까지' : '접수 마감됨',
        kind: 'registration-closing',
      };
    }
    const dr = daysBetween(raceDate, now);
    return isNaN(dr) ? MISSING : buildRaceDayInfo(dr);
  }

  if (race.status === 'upcoming') {
    const d = daysBetween(regStart, now);
    if (!isNaN(d)) {
      return {
        days: d,
        label: toLabel(d),
        caption: d === 0 ? '오늘 접수 시작' : d > 0 ? '접수 시작까지' : '접수 진행 중',
        kind: 'registration-opening',
      };
    }
    const dr = daysBetween(raceDate, now);
    return isNaN(dr) ? MISSING : buildRaceDayInfo(dr);
  }

  if (race.status === 'closed') {
    const d = daysBetween(raceDate, now);
    return isNaN(d) ? MISSING : buildRaceDayInfo(d);
  }

  // past / completed — label 자리에 "얼마나 지났는지"를 보여 주고,
  // caption 은 "대회 종료"로 고정해 상태 뱃지("완료")와 중복되지 않게 한다.
  const d = daysBetween(raceDate, now);
  return isNaN(d) ? MISSING : buildPastInfo(d);
}
